import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { DropdownItem } from '../../components/input';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { DocumentUploadFileState } from './documentUploadFileState';
import { DocumentUploadWorkflow } from './documentUploadGuidedTrainingState';
import { JobSpeciality } from '@clipr/lib';
import { WarrantUploadState } from './warrantUploadState';

export type DocumentUploadPageParams = {
  teamId?: string;
};

export type DocumentUploadMode = DocumentUploadWorkflow | 'subpoena';

export class DocumentUploadPageState
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      selectedTeamId: observable,
      mode: observable,
      teamId: computed,
      team: computed,
      destinationItems: computed,
      draftComponent: computed,
      activeUploadState: computed,
      showStaging: computed,
      stagedCount: computed,
      mounted: action,
      unmounted: action,
      setSelectedTeamId: action,
      selectMode: action,
      setMode: action,
    });

    this.fileComponent = new DocumentUploadFileState(this.store, {
      teamId: () => this.teamId,
    });
    this.warrantComponent = new WarrantUploadState(this.store, {
      teamId: () => this.teamId,
    });
    this.subpoenaComponent = new WarrantUploadState(this.store, {
      teamId: () => this.teamId,
      speciality: JobSpeciality.SubpoenaRequestDraft,
    });
  }

  readonly fileComponent: DocumentUploadFileState;
  readonly warrantComponent: WarrantUploadState;
  readonly subpoenaComponent: WarrantUploadState;

  selectedTeamId: string | null = null;
  mode: DocumentUploadMode | null = null;

  private mountVersion = 0;

  get teamId(): string | undefined {
    return this.selectedTeamId ?? undefined;
  }

  get team() {
    return this.teamId ? this.store.teamManager.getTeam(this.teamId) : null;
  }

  get destinationItems(): DropdownItem[] {
    return this.store.teamManager.teamsSortedByName
      .filter(candidate => candidate.hasPermission('CreateJob'))
      .map(candidate => ({ value: candidate.id, label: candidate.displayName || candidate.name }));
  }

  get draftComponent(): WarrantUploadState | null {
    if (this.mode === 'warrant')
      return this.warrantComponent;
    return this.mode === 'subpoena' ? this.subpoenaComponent : null;
  }

  get activeUploadState() {
    return this.draftComponent ?? this.fileComponent;
  }

  get showStaging(): boolean {
    return this.draftComponent?.showStagingForm
      ?? (this.fileComponent.showStagingForm || this.fileComponent.showProgressBox);
  }

  get stagedCount(): number {
    return (this.draftComponent ?? this.fileComponent).stagedItems.length;
  }

  async mounted(params: DocumentUploadPageParams) {
    const currentMountVersion = ++this.mountVersion;
    this.selectedTeamId = params.teamId ?? null;

    await this.store.teamManager.apiEnsureTeams();
    if (currentMountVersion !== this.mountVersion)
      return;

    if (!params.teamId) {
      const defaultLibraryId = this.resolveDefaultLibraryId();
      if (defaultLibraryId)
        runInAction(() => {
          if (currentMountVersion === this.mountVersion)
            this.selectedTeamId = defaultLibraryId;
        });
    }

    this.fileComponent.init();
    // The training walks through uploading, which needs a library to upload into.
    if (!this.destinationItems.length)
      return;
    this.store.documentUploadGuidedTraining.registerUploadPage(this.guidedTrainingContext);
    this.store.documentUploadGuidedTraining.handleUploadPageMounted();
  }

  unmounted() {
    ++this.mountVersion;
    this.store.documentUploadGuidedTraining.handleUploadPageUnmounted();
    this.store.documentUploadGuidedTraining.unregisterUploadPage(this.guidedTrainingContext);
    this.fileComponent.reset();
    this.warrantComponent.reset();
    this.subpoenaComponent.reset();
    this.selectedTeamId = null;
    this.mode = null;
  }

  setSelectedTeamId(teamId: string | null) {
    this.selectedTeamId = teamId;
  }

  selectMode(workflow: DocumentUploadMode) {
    this.mode = workflow;
    if (workflow !== 'subpoena')
      this.store.documentUploadGuidedTraining.handleWorkflowSelected(workflow);
  }

  setMode(workflow: DocumentUploadWorkflow) {
    this.mode = workflow;
  }

  handleBackButton(prevRoute: string | null) {
    if (prevRoute)
      window.history.back();
  }

  private resolveDefaultLibraryId(): string | null {
    const email = this.store.user?.email?.toLowerCase();
    if (!email)
      return null;

    return this.store.teamManager.teams.find(
      team => team.externalEmail?.toLowerCase() === email && team.hasPermission('CreateJob')
    )?.id ?? null;
  }

  private readonly guidedTrainingContext = {
    getTeamId: () => this.teamId ?? null,
    getWorkflow: () => (this.mode === 'subpoena' ? null : this.mode),
    // Training restores its workflow once the page finishes loading; a choice made meanwhile wins.
    setWorkflow: (workflow: DocumentUploadWorkflow) => this.mode ?? this.setMode(workflow),
    getTranslationState: () => this.fileComponent,
    getWarrantState: () => this.warrantComponent,
  };
}
